import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../i18n/I18nContext'
import { AuthContext } from '../../context/AuthContext'
import { getEnvironment, getPersonaDirectory, revokePersonaSessions, setEnvironment, switchPersona, type PersonaDirectory } from '../../services/environment'

const copy = {
  en: { banner: 'DEMO — Sales Showcase (synthetic data)', toMain: 'Switch to Main', persona: 'Demo persona', choose: 'Choose a persona', switch: 'Switch',
    code: 'Your authenticator code', codeHint: 'Required for MFA-protected personas', revoke: 'End all persona sessions', revoked: 'persona sessions ended',
    presenter: 'presenter', current: 'current', failed: 'Switch refused', returnToPresenter: 'Return to presenter' },
  fr: { banner: 'DÉMO — Vitrine commerciale (données fictives)', toMain: 'Passer à Principal', persona: 'Persona de démo', choose: 'Choisir une persona', switch: 'Changer',
    code: 'Votre code d’authentification', codeHint: 'Requis pour les personas protégées par MFA', revoke: 'Terminer toutes les sessions de persona', revoked: 'sessions de persona terminées',
    presenter: 'présentateur', current: 'actuelle', failed: 'Changement refusé', returnToPresenter: 'Revenir au présentateur' },
}

/** Visible only in the Demo environment. The persona switcher appears only for an authorized presenter (server decides). */
export const DemoEnvironmentBar: React.FC = () => {
  const { locale } = useI18n()
  const text = copy[String(locale).startsWith('fr') ? 'fr' : 'en']
  const [environment, setEnvironmentState] = useState<string | null>(null)
  const [directory, setDirectory] = useState<PersonaDirectory | null>(null)
  const [target, setTarget] = useState('')
  const [code, setCode] = useState('')
  const [message, setMessage] = useState('')
  // Re-evaluated whenever the signed-in user changes (sign-in, persona switch, sign-out); the server decides eligibility.
  const signedInUserId = useContext(AuthContext)?.user?.id || null
  useEffect(() => { void getEnvironment().then(state => {
    setEnvironmentState(state?.environment || null)
    if (state?.environment === 'demo' && signedInUserId) void getPersonaDirectory().then(setDirectory)
    else setDirectory(null)
  }) }, [signedInUserId])
  const presenterAccount = useMemo(() => directory?.groups.flatMap(group => group.roles.flatMap(role => role.accounts)).find(account => account.presenter), [directory])
  if (environment !== 'demo') return null
  const go = async (userId: string) => {
    setMessage('')
    try { await switchPersona(userId, code || undefined) }
    catch (error: any) { setMessage(`${text.failed}: ${error?.response?.data?.message || error?.response?.data?.code || ''}`) }
  }
  return (
    <div data-testid="demo-environment-bar" role="region" aria-label={text.banner}
      style={{ background: '#7a2e0e', color: '#fff', padding: '6px 12px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: 14 }}>
      <strong data-testid="demo-environment-label">{text.banner}</strong>
      {directory && (
        <form data-testid="persona-switcher" onSubmit={event => { event.preventDefault(); if (target) void go(target) }} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <label htmlFor="demoPersona">{text.persona}</label>
          <select id="demoPersona" aria-label={text.persona} value={target} onChange={event => setTarget(event.target.value)} style={{ maxWidth: 360, color: '#111' }}>
            <option value="">{text.choose}</option>
            {directory.groups.map(group => group.roles.map(role => (
              <optgroup key={`${group.organization}·${role.role}`} label={`${group.organization} · ${role.role}`}>
                {role.accounts.map(account => <option key={account.userId} value={account.userId}>
                  {account.email}{account.presenter ? ` (${text.presenter})` : ''}{account.current ? ` (${text.current})` : ''}
                </option>)}
              </optgroup>
            )))}
          </select>
          <input id="presenterCode" aria-label={text.code} title={text.codeHint} placeholder={text.code} inputMode="numeric" autoComplete="one-time-code"
            value={code} onChange={event => setCode(event.target.value)} style={{ width: 150, color: '#111' }} />
          <button type="submit" className="btn btn-secondary" disabled={!target}>{text.switch}</button>
          {!directory.actingAsPresenter && presenterAccount && <button type="button" className="btn btn-secondary" onClick={() => void go(presenterAccount.userId)}>{text.returnToPresenter}</button>}
          <button type="button" className="btn btn-secondary" onClick={() => void revokePersonaSessions().then(count => setMessage(`${count} ${text.revoked}`), () => setMessage(text.failed))}>{text.revoke}</button>
        </form>
      )}
      <button type="button" className="btn btn-secondary" data-testid="switch-to-main" onClick={() => void setEnvironment('main')} style={{ marginLeft: 'auto' }}>{text.toMain}</button>
      {message && <span role="status" data-testid="persona-message">{message}</span>}
    </div>
  )
}
